import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import CopyButton from '../CopyButton/CopyButton';
import './WalletRotationGate.scss';

/** Require server-persisted acknowledgement before mounting the working panel. */
export default function WalletRotationGate({ children }) {
    const { t } = useTranslation('balance');
    const apiUrl = useSelector(state => state.API_URL);
    const token = localStorage.getItem('accessToken');
    const [status, setStatus] = useState(null);
    const [checked, setChecked] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(false);
    const current = useRef(null);
    const running = useRef(false);
    const alive = useRef(true);
    const controller = useRef(null);
    const epoch = useRef(0);

    const request = useCallback(async (path, method = 'GET') => {
        const abort = new AbortController();
        controller.current = abort;
        const timer = setTimeout(() => abort.abort(), 15000);
        try {
            const response = await fetch(`${apiUrl}${path}`, {
                method, signal: abort.signal,
                headers: { Authorization: `JWT ${token}` },
            });
            if (!response.ok) throw new Error('wallet notice unavailable');
            return await response.json();
        } finally {
            clearTimeout(timer);
            if (controller.current === abort) controller.current = null;
        }
    }, [apiUrl, token]);

    const loadStatus = useCallback(async (generation) => {
        const data = await request('wallet-rotation/');
        if (!data || !Object.prototype.hasOwnProperty.call(data, 'notice')
            || (data.current_address !== null && typeof data.current_address !== 'string')
            || (data.notice !== null && (!Number.isInteger(data.notice?.id) || data.notice.id < 1))) {
            throw new Error('invalid wallet notice');
        }
        if (!alive.current || generation !== epoch.current) return;
        if (data.notice?.id !== current.current?.notice?.id
            || data.current_address !== current.current?.current_address) setChecked(false);
        current.current = data;
        setStatus(data);
        setError(false);
    }, [request]);

    const checkStatus = useCallback(async () => {
        if (running.current) return;
        running.current = true;
        const generation = epoch.current;
        setBusy(true);
        try {
            await loadStatus(generation);
        } catch (_) {
            if (alive.current && generation === epoch.current) {
                setError(true);
                setChecked(false);
            }
        } finally {
            if (generation === epoch.current) {
                running.current = false;
                if (alive.current) setBusy(false);
            }
        }
    }, [loadStatus]);

    useEffect(() => {
        alive.current = true;
        current.current = null;
        setStatus(null);
        setChecked(false);
        checkStatus();
        const interval = setInterval(checkStatus, 30000);
        window.addEventListener('focus', checkStatus);
        return () => {
            alive.current = false;
            epoch.current += 1;
            running.current = false;
            clearInterval(interval);
            window.removeEventListener('focus', checkStatus);
            controller.current?.abort();
        };
    }, [checkStatus]);

    const acknowledge = async () => {
        const notice = current.current;
        if (!checked || error || running.current || !notice?.notice || !notice.current_address) return;
        running.current = true;
        const generation = epoch.current;
        setBusy(true);
        try {
            await request(`wallet-rotation/${notice.notice.id}/ack/`, 'POST');
            // Never unlock on a local click or even on POST alone. Another
            // rotation may have completed while this acknowledgement was sent.
            await loadStatus(generation);
        } catch (_) {
            if (alive.current && generation === epoch.current) {
                setError(true);
                setChecked(false);
            }
        } finally {
            if (generation === epoch.current) {
                running.current = false;
                if (alive.current) setBusy(false);
            }
        }
    };

    if (status && !status.notice && !error) return children;
    const changed = Boolean(status?.notice);
    return (
        <Modal isOpen shouldCloseOnEsc={false} shouldCloseOnOverlayClick={false}
            appElement={document.getElementById('root') || undefined}
            className="confirmation-modal wallet-rotation-gate"
            overlayClassName="confirmation-modal-overlay wallet-rotation-gate-overlay"
            aria={{ labelledby: 'wallet-rotation-gate-title', describedby: 'wallet-rotation-gate-message' }}>
            <div className="confirmation-modal-header">
                <h2 id="wallet-rotation-gate-title" className="confirmation-modal-title">
                    {t(changed ? 'rotationTitle' : 'rotationChecking')}
                </h2>
            </div>
            <div className="confirmation-modal-content">
                <p id="wallet-rotation-gate-message">
                    {t(changed ? 'rotationBlockingMessage' : 'rotationCheckingMessage')}
                </p>
                {changed && status.current_address && !error && <div className="confirmation-modal-info-block">
                    <strong>{t('currentAddress')}</strong>
                    <code className="wallet-rotation-gate__address">{status.current_address}</code>
                    <CopyButton text={status.current_address} />
                </div>}
                {changed && !status.current_address && <p role="alert">{t('rotationAddressUnavailable')}</p>}
                {changed && <label className="wallet-rotation-gate__checkbox">
                    <input type="checkbox" checked={checked} disabled={busy || error || !status.current_address}
                        onChange={event => setChecked(event.target.checked)} />
                    <span>{t('rotationConfirmCheckbox')}</span>
                </label>}
                {error && <p role="alert" className="confirmation-modal-error">{t('rotationCheckError')}</p>}
            </div>
            <div className="confirmation-modal-actions">
                {(error || (changed && !status.current_address)) && <button type="button"
                    className="confirmation-modal-button cancel" disabled={busy} onClick={checkStatus}>
                    {t('rotationRetry')}
                </button>}
                {changed && <button type="button" className="confirmation-modal-button confirm-danger"
                    disabled={!checked || busy || error || !status.current_address} onClick={acknowledge}>
                    {t('rotationConfirmContinue')}
                </button>}
            </div>
        </Modal>
    );
}
